import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_SNACK_BAR_DATA,
  MatSnackBarAction,
  MatSnackBarActions,
  MatSnackBarLabel,
  MatSnackBarRef,
} from '@angular/material/snack-bar';
import { TranslatePipe } from '@ngx-translate/core';

import { SNACKBAR_DURATION } from '@shared/global/globals';

export type SnackbarType = 'success' | 'error' | 'info' | 'warning';

export interface SnackbarData {
  /** Translation key for the notification text — resolved by the component. */
  message: string;
  /** Interpolation values for `message`, e.g. `{ name }` for a `'{{name}}'` placeholder. */
  params?: Record<string, unknown>;
  /** Drives the background color of the panel. */
  type: SnackbarType;
  /**
   * Paces the countdown bar; defaults to `SNACKBAR_DURATION`. It has to match
   * `MatSnackBarConfig.duration`, which is what actually dismisses the popup —
   * set one without the other and the bar desynchronizes from the dismissal.
   * `SnackbarService` sets both from a single value; prefer it to opening this
   * component by hand.
   */
  duration?: number;
}

/**
 * Notification popup rendered in a `MatSnackBar` overlay. Panel position,
 * duration and panel class come from `MAT_SNACK_BAR_DEFAULT_OPTIONS` (see
 * `snackbarDefaultOptions` in `app.config.ts`).
 *
 * Features do not open this component directly — `SnackbarService` is the entry
 * point, and the only thing that keeps the two durations in step:
 *
 * ```ts
 * inject(SnackbarService).success('views.projects.messages.saved');
 * ```
 */
@Component({
  imports: [
    MatButtonModule,
    MatSnackBarLabel,
    MatSnackBarActions,
    MatSnackBarAction,
    TranslatePipe,
    NgClass,
  ],
  selector: 'app-snackbar',
  styleUrl: './snackbar.scss',
  templateUrl: './snackbar.html',
})
export class SnackbarComponent {
  snackBarRef = inject(MatSnackBarRef);
  data: SnackbarData = inject<SnackbarData>(MAT_SNACK_BAR_DATA);

  /**
   * How long the countdown bar takes to fill, as a CSS duration. The bar is one
   * continuous CSS animation rather than a value stepped from JS: every widget
   * that animates value changes (`mat-progress-bar` transitions them over
   * 250ms) renders a stepped bar permanently behind its own value, so it never
   * visibly reaches full before the popup goes.
   */
  protected readonly countdownDuration = `${this.data.duration ?? SNACKBAR_DURATION}ms`;

  /**
   * Whether the countdown has started. It must not start when this component is
   * constructed: Material arms the auto-dismiss timer on `afterOpened()`, which
   * fires only after its 150ms enter animation
   * (`MatSnackBar.openFromComponent` -> `_dismissAfter`). Starting any earlier
   * is what makes the bar finish ahead of the dismissal.
   */
  protected readonly running = signal(false);

  constructor() {
    this.snackBarRef
      .afterOpened()
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.running.set(true));
  }
}
